import type { ColumnFiltersState } from '@tanstack/react-table';

import { getAdminBrandsQueryKey } from '@r/api/generated/client.ts';
import { customInstance } from '@r/api/orval-axios-mutator.ts';
import type {
  AdminBrandsParams,
  ProductBrandProductBrandReadModel
} from '@r/api/generated/client.schemas.ts';

export type CustomShopBrandsGetCollectionParams = AdminBrandsParams & {
  name?: string;
  search?: string;
};

/** Convertit les filtres TanStack Table en paramètres de recherche API. */
export function buildCustomShopBrandCollectionParams(
  columnFilters: ColumnFiltersState
): CustomShopBrandsGetCollectionParams {
  const params: CustomShopBrandsGetCollectionParams = {};

  const search = String(columnFilters.find((filter) => filter.id === 'search')?.value ?? '').trim();
  if (search) {
    params.search = search;
  }

  return params;
}

/** Clé React Query stable pour la liste des marques d'une boutique. */
export function getAdminShopBrandsCatalogQueryKey(
  shopId: string | number,
  params?: CustomShopBrandsGetCollectionParams
) {
  return getAdminBrandsQueryKey(String(shopId), params as AdminBrandsParams | undefined);
}

export type ProductBrandWriteModel = {
  name: string;
};

/** IRI ProductBrand attendue dans les payloads produit (POST/PATCH). */
export function getProductBrandIri(brandId: number): string {
  return `/product_brands/${brandId}`;
}

/** Détecte un doublon de nom de marque (comparaison insensible à la casse). */
export function hasBrandNameConflict(
  brands: readonly { id: number; name: string }[],
  name: string,
  excludeBrandId?: number
): boolean {
  const normalizedName = name.trim().toLocaleLowerCase();
  if (!normalizedName) return false;

  return brands.some(
    (brand) =>
      brand.id !== excludeBrandId && brand.name.trim().toLocaleLowerCase() === normalizedName
  );
}

/** Crée une marque via l'endpoint admin de la boutique. */
export async function apiAdminshopsShopIdbrandsPost(
  shopId: string,
  data: ProductBrandWriteModel
): Promise<ProductBrandProductBrandReadModel> {
  return customInstance<ProductBrandProductBrandReadModel>({
    url: `/admin/shops/${encodeURIComponent(shopId)}/brands`,
    method: 'POST',
    data
  });
}

/** Met à jour une marque existante via l'endpoint admin de la boutique. */
export async function apiAdminshopsShopIdbrandsIdPatch(
  shopId: string,
  brandId: string,
  data: ProductBrandWriteModel
): Promise<ProductBrandProductBrandReadModel> {
  return customInstance<ProductBrandProductBrandReadModel>({
    url: `/admin/shops/${encodeURIComponent(shopId)}/brands/${encodeURIComponent(brandId)}`,
    method: 'PATCH',
    data
  });
}

/** Liste les marques d'une boutique avec filtres optionnels (recherche, nom). */
export async function apiShopsShopIdbrandsGetCollectionWithFilters(
  shopId: string,
  params?: CustomShopBrandsGetCollectionParams,
  signal?: AbortSignal
): Promise<ProductBrandProductBrandReadModel[]> {
  return customInstance<ProductBrandProductBrandReadModel[]>({
    url: `/admin/shops/${encodeURIComponent(String(shopId))}/brands`,
    method: 'GET',
    params,
    signal
  });
}
